import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  ButtonGroup,
  TablePagination,
  InputBase,
  IconButton,
  Snackbar,
  Alert,
  Paper,
} from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CreateIcon from "@mui/icons-material/Create";
import SearchIcon from "@mui/icons-material/Search";
import ActualizarUsuario from "./actualizarUsuarios";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";

interface Rol {
  id: number;
  nombre: string;
  descripcion: string;
}

interface UsuarioData {
  id: number;
  nombre: string;
  usuario: string;
  correo: string;
  rol: Rol;
}

export default function TablaUsuarios() {
  const [data, setData] = useState<UsuarioData[]>([]);
  const [page, setPage] = useState(0); // Estado para la página actual
  const [rowsPerPage, setRowsPerPage] = useState(10); // Estado para las filas por página
  const [searchTerm, setSearchTerm] = useState("");
  const [successMessage, setSuccessMessage] = useState(false);

  const [modalOpen, setModalOpen] = useState(false); // Estado para el modal
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);

  const tokenBase64 = localStorage.getItem("authToken"); // Suponiendo que el token está almacenado con la clave 'authToken'
  const token = tokenBase64 ? atob(tokenBase64) : ""; // Decodifica el token de Base64

  // Función para obtener los datos de la API
  const fetchTableData = async () => {
    try {
      const response = await fetch(
        `${getProyectosApiBaseUrl()}/usuarios/obtenerUsuariosProyectos`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error al obtener los datos");
      }

      const data = await response.json();
      setData(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Cargar los datos cuando el componente se monta
  useEffect(() => {
    fetchTableData();
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleUpdate = (id: number) => {
    if (id !== undefined) {
      setSelectedId(id);
      setModalOpen(true); // Abre el modal
    } else {
      console.error("ID no válido para la actualización.");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await fetch(
        `${getProyectosApiBaseUrl()}/usuarios/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error al eliminar el registro");
      }

      console.log(`Registro con ID ${id} eliminado exitosamente`);

      // Actualizar los datos de la tabla después de eliminar
      fetchTableData();

      // Mostrar mensaje de éxito
      setSuccessMessage(true);
    } catch (error) {
      console.error("Error al eliminar el registro:", error);
    }
  };

  const handleCloseSuccessMessage = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSuccessMessage(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const filteredData = data.filter(
    (item) =>
      item.nombre.toLowerCase().includes(searchTerm) ||
      item.usuario.toLowerCase().includes(searchTerm) ||
      String(item.correo || "").toLowerCase().includes(searchTerm) ||
      item.rol.nombre.toLowerCase().includes(searchTerm)
  );

  const handleClose = () => {
    setModalOpen(false);
    setSelectedId(undefined); // Cierra el diálogo y restablece selectedId a undefined
  };

  return (
    <Paper elevation={16}>
      <div style={{ display: "flex", alignItems: "center", padding: "10px" }}>
        <InputBase
          sx={{ ml: 1, flex: 1 }}
          placeholder="Buscar"
          inputProps={{ "aria-label": "search" }}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <IconButton type="button" sx={{ p: "10px" }} aria-label="search">
          <SearchIcon />
        </IconButton>
      </div>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>Nombre</strong></TableCell>
            <TableCell><strong>Usuario</strong></TableCell>
            <TableCell><strong>Correo</strong></TableCell>
            <TableCell><strong>Rol</strong></TableCell>
            <TableCell><strong>Acciones</strong></TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {filteredData
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.nombre}</TableCell>
                <TableCell>{item.usuario}</TableCell>
                <TableCell>{item.correo || "—"}</TableCell>
                <TableCell>{item.rol.nombre}</TableCell>

                <TableCell>
                  <ButtonGroup
                    variant="contained"
                    aria-label="Basic button group"
                  >
                    <Button color="error" onClick={() => handleDelete(item.id)}>
                      <RemoveCircleOutlineIcon />
                    </Button>
                    <Button
                      color="primary"
                      onClick={() => handleUpdate(item.id)}
                    >
                      <CreateIcon />
                    </Button>
                  </ButtonGroup>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Snackbar para mostrar el mensaje de éxito */}
      <Snackbar
        open={successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSuccessMessage}
      >
        <Alert
          onClose={handleCloseSuccessMessage}
          severity="success"
          sx={{ width: "100%" }}
        >
          Registro eliminado exitosamente
        </Alert>
      </Snackbar>
      <ActualizarUsuario
        open={modalOpen}
        onClose={handleClose}
        id={selectedId}
        onSubmit={fetchTableData}
      />
    </Paper>
  );
}
